import type { MetricDefinition } from '../types/dashboard';

const MEMBER_FILTERS = ['Acquisition date range', 'Territory', 'AOP', 'FBC / AD', 'Acquisition source'];
const REG_FILTERS = [...MEMBER_FILTERS, 'Sport', 'Program type', 'Program season', 'Program year'];
const RETENTION_FILTERS = ['Territory', 'AOP', 'FBC / AD', 'Sport', 'Program type', 'Base & comparison season'];

/**
 * Metric reference for the Power BI scoping prototype.
 * Statuses reflect the business review responses; nothing here is approved production logic
 * unless marked Confirmed.
 */
export const metricDefinitions: MetricDefinition[] = [
  {
    id: 'members',
    name: 'New Members',
    definition:
      'Members newly associated with a franchise during the selected date range — not the total existing member database. Potential and current customers within an AOP whose details were collected via the website, a paid ad or local marketing.',
    calculation:
      'COUNT DISTINCT MemberID per franchise / territory with a franchise association date inside the selected range. The same MemberID may count in more than one franchise; network total = SUM of franchise-level counts.',
    grain: 'MemberID × Franchise',
    identifier: 'MemberID (not email or household ID)',
    dateBasis: 'Date the member was first associated with the franchise (preferred) — not necessarily the corporate member creation date',
    filters: MEMBER_FILTERS,
    status: 'Pending validation',
    statusDetail: 'Identifier: Proposed — technical validation required. Date added: pending technical validation. Multi-territory counting: Confirmed.',
    note: 'Prototype keeps its existing illustrative acquisition dates. Sport, program type, program season and year never filter members — there are no program-interest fields before registration (Confirmed).',
    openItems: [
      'Nick: confirm the member-to-franchise relationship and identifier used in the existing Power BI Member Stats report.',
      'Nick: confirm which date field drives member acquisition reporting today.',
    ],
  },
  {
    id: 'registrations',
    name: 'Registrations',
    definition: 'Program registrations / enrollments. One member can produce several registrations (e.g. a parent registering three children).',
    calculation:
      'Reuse the existing Power BI Member Stats registration-count measure (derived from transaction data) as the baseline, limited to registrations dated in the selected range by members acquired in that range. Prototype: illustrative counts only.',
    grain: 'Registration (business meaning) · transaction-derived (technical)',
    identifier: 'Per existing Member Stats measure — not a simple distinct RegistrationID',
    dateBasis: 'Registration / transaction date inside the selected range',
    filters: REG_FILTERS,
    status: 'Pending validation',
    statusDetail: 'Reuse existing Power BI logic — validation in progress.',
    note: 'No new exclusion rules are applied for cancellations, refunds, waitlists, partial payments or recurring registrations; the existing report’s treatment is preserved until reconciliation is complete.',
    openItems: ['Nick: final confirmation of counting logic and exclusions.'],
  },
  {
    id: 'selectedPeriodCohort',
    name: 'Selected-Period Cohort Rule (Funnel)',
    definition:
      'The funnel uses members acquired in the selected date range and checks which of them registered within that same range.',
    calculation:
      'Cohort = members acquired in range. Registered = cohort members with a qualifying registration in range. No all-time aggregate toggle at this stage.',
    grain: 'MemberID × Franchise',
    identifier: 'MemberID',
    dateBasis: 'Acquisition date and registration date both inside the selected range',
    filters: REG_FILTERS,
    status: 'Confirmed rule · build pending',
    statusDetail: 'Confirmed direction — implementation validation required.',
    note: 'This differs from counting all registrations made during the period regardless of when the member was acquired; those registrations are not shown in this funnel.',
    supporting: true,
  },
  {
    id: 'conversion',
    name: 'Conversion Rate',
    definition: 'Share of the selected acquisition cohort who registered during the applicable conversion window.',
    calculation:
      'DISTINCT MemberIDs in the acquisition cohort with a qualifying registration in the window ÷ DISTINCT MemberIDs in the cohort. Monthly trend: same-month acquisition and conversion. Selected-period KPI: members acquired earlier in the period may convert later within the period.',
    grain: 'MemberID × Franchise',
    identifier: 'MemberID',
    dateBasis: 'Acquisition date and qualifying registration date inside the window',
    filters: REG_FILTERS,
    status: 'Confirmed rule · build pending',
    statusDetail: 'Confirmed business definition — technical validation required.',
    note: 'The selected-period rate is usually higher than the individual monthly rates because conversions later in the period count. Kept separate from Registrations per Member: a parent registering three children is one converted member.',
    openItems: ['Use the same qualifying-registration rules validated for Member Stats.'],
  },
  {
    id: 'regsPerMember',
    name: 'Registrations per Member',
    definition: 'Registration frequency among registering members. Not a member conversion percentage.',
    calculation: 'Total qualifying registrations ÷ DISTINCT MemberIDs contributing those registrations.',
    grain: 'Ratio',
    identifier: 'MemberID (denominator)',
    dateBasis: 'Same window as Conversion Rate',
    filters: REG_FILTERS,
    status: 'Confirmed rule · build pending',
    statusDetail: 'Confirmed concept — technical validation required.',
    openItems: ['Use the same qualifying registration logic as the validated Power BI measure.'],
  },
  {
    id: 'revenue',
    name: 'Revenue',
    definition: 'Registration revenue, intended to align with an established i9 Sports revenue reporting calculation.',
    calculation:
      'Final: reuse an existing Power BI or FM revenue measure (to be identified). Prototype: illustrative gross registration revenue for the cohort registrations — NOT an approved definition.',
    grain: 'Per existing revenue measure',
    identifier: 'To be confirmed',
    dateBasis: 'Pending: transaction date, registration date or another reporting date',
    filters: REG_FILTERS,
    status: 'Pending validation',
    statusDetail: 'Pending technical validation.',
    note: 'Prototype revenue figures are illustrative only and will not reconcile with existing revenue reporting.',
    openItems: [
      'Nick: which existing Power BI or FM revenue measure to reuse.',
      'Nick: handling of refunds, discounts, credits, fees and payment adjustments.',
      'Nick: attribution date (transaction, registration or other).',
      'Nick: reconciliation approach with existing revenue reporting.',
    ],
  },
  {
    id: 'seasonRetention',
    name: 'Season Retention',
    definition: 'Share of a base season’s participants who also registered in the comparison season.',
    calculation:
      'DISTINCT PlayerIDs in both the base and comparison seasons ÷ DISTINCT PlayerIDs in the base season. Default comparison: next chronological season; a later season can be selected.',
    grain: 'PlayerID, deduplicated within season',
    identifier: 'PlayerID (not MemberID or registration rows)',
    dateBasis: 'Season and year of the registered program — never the purchase date',
    filters: RETENTION_FILTERS,
    status: 'Confirmed rule · build pending',
    statusDetail: 'Confirmed concept — comparison rule pending. Season tagging: confirmed business rule, technical implementation pending.',
    note: 'A participant with several registrations in one season counts once in that season’s cohort.',
    openItems: [
      'Jeremy: must the comparison always be the next season, or any selected later season?',
      'Nick: verify the program season / year fields and relationships.',
      'Nick: confirm PlayerID relationships and duplicate-player handling.',
    ],
  },
  {
    id: 'yoyRetention',
    name: 'YOY Retention',
    definition: 'Participants from a base cohort who return and register again within the following 12 months.',
    calculation:
      'Primary: DISTINCT base-cohort PlayerIDs with another qualifying program registration within 12 months ÷ DISTINCT base-cohort PlayerIDs. Optional view: same season next year (e.g. Fall 2025 → Fall 2026).',
    grain: 'PlayerID, deduplicated within season',
    identifier: 'PlayerID',
    dateBasis: 'Program season / year of the base registration; any qualifying program in the next 12 months',
    filters: RETENTION_FILTERS,
    status: 'Pending validation',
    statusDetail: 'Pending business confirmation.',
    note: 'Prototype defaults to the 12-month view. Same-season-next-year is not assumed to be the default.',
    openItems: ['Jeremy: confirm the default YOY window.'],
  },
  {
    id: 'aop',
    name: 'AOP (Area of Play)',
    definition: 'The 10–15 minute drive time around a venue where a program is marketed. Named after a city, neighborhood, county or similar.',
    calculation: 'ZIP codes associated with a listed Area of Play in i9 Sports Connect. Members and registrations roll up to an AOP by ZIP code.',
    grain: 'ZIP code → AOP → Territory',
    identifier: 'ZIP code',
    dateBasis: 'Preferred: historical ZIP assignment at the time of activity. Prototype: current assignment only.',
    filters: ['Territory', 'AOP', 'FBC / AD'],
    status: 'Pending validation',
    statusDetail: 'AOP definition confirmed. ZIP reassignment history: pending technical and business validation.',
    note: 'If only current assignments exist, history is shown under current AOPs and that limitation must be labeled — it is not true historical attribution.',
    openItems: ['Nick: are historical ZIP-to-AOP and ZIP-to-territory relationships available?'],
  },
  {
    id: 'acquisitionSource',
    name: 'Acquisition Source',
    definition: 'Member referrer values grouped into Digital, Local / Other and Unknown / Unclassified.',
    calculation:
      'Digital = referrers explicitly classified as digital. Local / Other = referrers classified as local or other non-digital. Unknown / Unclassified = unmapped or ambiguous referrers — never defaulted to Local. Registrations and revenue use the associated member’s classification.',
    grain: 'MemberID',
    identifier: 'Member referrer value',
    dateBasis: 'Acquisition date',
    filters: MEMBER_FILTERS,
    status: 'Pending validation',
    statusDetail: 'Pending mapping definition.',
    note: 'Digital / Local charts and the Unknown share in this prototype are illustrative; no confirmed mapping exists yet. Not applied to retention.',
    openItems: [
      'Nick + business team: final referrer-to-group mapping and treatment of Unknown records.',
      'Validate attributing registrations and revenue to the member’s referrer group.',
    ],
    supporting: true,
  },
  {
    id: 'networkAverage',
    name: 'Network Avg per Active Territory',
    definition: 'Benchmark used for variance and status. AOP views divide by active AOPs instead.',
    calculation:
      'Monthly: network metric total ÷ active territories in that month. KPI card: latest completed month. Selected-period summary: mean of the monthly per-territory values (labeled “monthly mean”) — not the period total ÷ territories active at period end.',
    grain: 'Territory or AOP × Month',
    identifier: 'Franchise / territory ID',
    dateBasis: 'Each reporting month; a territory is compared only on months it was eligible',
    filters: REG_FILTERS,
    status: 'Proposed',
    statusDetail: 'Proposed — pending denominator validation.',
    openItems: ['Nick: validate the active-territory denominator and whether the monthly mean is the preferred benchmark.'],
    supporting: true,
  },
  {
    id: 'activeTerritory',
    name: 'Active Territory',
    definition: 'A territory eligible for active-territory reporting in a given month.',
    calculation:
      'Not active before its activation date; eligible from its activation month. Must not be assumed active indefinitely — closure, transfer or deactivation may end eligibility.',
    grain: 'Territory × Month',
    identifier: 'Franchise ID (Snowflake Franchise table)',
    dateBasis: 'Franchise activation date (believed to be in the Snowflake Franchise table)',
    filters: ['Territory', 'FBC / AD'],
    status: 'Pending validation',
    statusDetail: 'Pending technical validation.',
    note: 'Sample data uses an illustrative activation month per territory and has no closures, so no territory drops out in the prototype.',
    openItems: [
      'Nick: confirm the meaning of the activation date.',
      'Nick: are there status, closure, transfer or deactivation fields to consider?',
    ],
    supporting: true,
  },
  {
    id: 'performanceStatus',
    name: 'Performance Status',
    definition: 'Rule-based comparison to the network benchmark for the selected metric. No scoring model.',
    calculation:
      'Counts: Below network ≥10% under the benchmark, Priority review ≥25% under, Above ≥10% over. Conversion and retention: 3 pp / 6 pp. Registrations per Member: 5% / 12%. Counts compare average per active month to the network monthly average.',
    grain: 'Territory or AOP',
    identifier: 'Franchise / territory ID',
    dateBasis: 'Selected date range',
    filters: REG_FILTERS,
    status: 'Illustrative',
    statusDetail: 'Illustrative — pending business approval. Not official i9 Sports coaching standards.',
    openItems: ['Jeremy + FBC team: set thresholds, possibly by franchise maturity or peer group.'],
    supporting: true,
  },
  {
    id: 'ramping',
    name: 'Ramping Territory',
    definition: 'A territory still within its initial ramp-up window after activation.',
    calculation: 'Provisional: fewer than 12 months between the validated activation date and the end of the selected range.',
    grain: 'Territory',
    identifier: 'Franchise ID',
    dateBasis: 'Activation date',
    filters: ['Territory', 'FBC / AD'],
    status: 'Proposed',
    statusDetail: 'Proposed — pending business approval.',
    note: 'Ramping territories stay visible in every table and toggle; their comparisons may later need a separate benchmark.',
    openItems: ['Jeremy: is 12 months the right ramp window?'],
    supporting: true,
  },
  {
    id: 'corporateDistinct',
    name: 'Company-Wide Distinct Members',
    definition: 'Distinct MemberIDs across the whole network, without franchise-level double counting.',
    calculation: 'Not calculated in this dashboard.',
    grain: 'MemberID',
    identifier: 'MemberID',
    dateBasis: '—',
    filters: [],
    status: 'Not in scope',
    statusDetail: 'Not required for initial scope.',
    note: 'The primary dashboard uses franchisee-level counts only and does not mix in a corporate distinct count.',
    supporting: true,
  },
];
